import React, { useMemo } from 'react';
import { View, Text, ScrollView, Pressable, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { ArrowLeft } from 'lucide-react-native';
import { formatKickoffTime } from '../../../src/worldcup/helpers/worldcup.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { getMatchPickStatsHeaderGradientColors, getWcColors } from './matchPickStats.helper';
import { MATCH_PICK_STATS_COPY, MATCH_PICK_STATS_TEST_IDS } from './MatchPickStats.constants';
import { getMatchPickStatsStyles } from './MatchPickStatsScreen.styles';
import { getMatchPickStatsViewStyles } from './MatchPickStatsView.styles';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { PickStatsTeamCrest } from './PickStatsTeamCrest';
import { PickStatsScopeToggle } from './PickStatsScopeToggle';
import { PickStatsOutcomePie } from './PickStatsOutcomePie';
import { PickStatsScoreGrid } from './PickStatsScoreGrid';
import { PickStatsOddsTable } from './PickStatsOddsTable';
import { PickStatsMemberPicksRow } from './PickStatsMemberPicksRow';
import { PickStatsScreenSkeleton } from './PickStatsScreenSkeleton';
import type { PickStatsScope } from '../../../src/worldcup/hooks/useMatchPickStats';
import type { GroupsApiEntry } from '../../../src/worldcup/hooks/useWorldCupGroupsQuery';
import type { MatchPickStatsData, PickStatsMatchInfo } from '../../../src/worldcup/helpers/matchPickStatsApi';

export interface MatchPickStatsViewProps {
  onBack: () => void;
  onEditPick?: () => void;
  data: MatchPickStatsData | null;
  isLoading: boolean;
  isError: boolean;
  viewerPick?: { home: number; away: number } | null;
  isPickEditable?: boolean;
  scope: PickStatsScope;
  groups: GroupsApiEntry[];
  selectedGroupId: string | null;
  onScopeChange: (scope: PickStatsScope) => void;
  onGroupChange: (groupId: string) => void;
  /**
   * Which crest the teams wear (AC-10.6). Defaults to 'flag' so every existing caller and
   * story is unchanged; the generic competition shell supplies 'kit'.
   */
  crestMode?: 'flag' | 'kit';
}

const LIVE_STATUSES = new Set(['LIVE', 'IN_PLAY', 'PAUSED']);
const HEADER_GRADIENT_START = { x: 0.5, y: 0 };
const HEADER_GRADIENT_END = { x: 0.5, y: 1 };

function getStatusInfo(matchInfo: PickStatsMatchInfo): { label: string; isLive: boolean } {
  const { status, liveMinute, kickoffTime } = matchInfo;
  if (status === 'FINISHED') return { label: 'Full Time', isLive: false };
  if (LIVE_STATUSES.has(status)) {
    return { label: liveMinute != null ? `${liveMinute}'` : 'Live', isLive: true };
  }
  const msLeft = new Date(kickoffTime).getTime() - Date.now();
  if (msLeft <= 0) return { label: 'Soon', isLive: false };
  const totalMin = Math.floor(msLeft / 60000);
  if (totalMin < 60) return { label: `In ${totalMin}m`, isLive: false };
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours < 24) return { label: `In ${hours}h${mins > 0 ? ` ${mins}m` : ''}`, isLive: false };
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  return { label: `In ${days}d${remH > 0 ? ` ${remH}h` : ''}`, isLive: false };
}

export const MatchPickStatsView: React.FC<MatchPickStatsViewProps> = ({
  onBack,
  onEditPick,
  data,
  isLoading,
  isError,
  viewerPick = null,
  isPickEditable = false,
  scope,
  groups,
  selectedGroupId,
  onScopeChange,
  onGroupChange,
  crestMode = 'flag',
}) => {
  const theme = useTheme();
  const C = useMemo(() => getWcColors(theme), [theme]);
  const styles = useMemo(() => getMatchPickStatsStyles(C), [C]);
  const vs = useMemo(() => getMatchPickStatsViewStyles(C), [C]);
  const headerGradientColors = useMemo(() => getMatchPickStatsHeaderGradientColors(C), [C]);

  if (isLoading) return <PickStatsScreenSkeleton />;

  const matchInfo = data?.match;
  const homeCountryCode = matchInfo?.homeCountryCode ?? null;
  const awayCountryCode = matchInfo?.awayCountryCode ?? null;
  // In kit mode the country code is deliberately ignored rather than used as a fallback:
  // for a club it resolves to its COUNTRY's flag, which is worse than no crest at all.
  const homeKit = crestMode === 'kit' && matchInfo ? getTeamKitConfig(matchInfo.homeTeam) : null;
  const awayKit = crestMode === 'kit' && matchInfo ? getTeamKitConfig(matchInfo.awayTeam) : null;
  const isFinished = matchInfo?.status === 'FINISHED';
  const isLive = LIVE_STATUSES.has(matchInfo?.status ?? '');
  const showFinishedSections = isFinished || isLive;
  const showGroupSections = showFinishedSections && scope === 'group' && !!data?.memberPicks;
  const statusInfo = matchInfo ? getStatusInfo(matchInfo) : null;
  const showPickFooter = matchInfo != null;
  const pickFooterTitle = viewerPick ? MATCH_PICK_STATS_COPY.YOUR_PICK : MATCH_PICK_STATS_COPY.NO_PICK_YET;
  const pickFooterText = viewerPick
    ? `${matchInfo?.homeTeam ?? MATCH_PICK_STATS_COPY.HOME_LABEL} ${viewerPick.home} – ${viewerPick.away} ${matchInfo?.awayTeam ?? MATCH_PICK_STATS_COPY.AWAY_LABEL}`
    : MATCH_PICK_STATS_COPY.MAKE_PICK_PROMPT;
  const pickFooterButtonLabel = isPickEditable
    ? viewerPick ? MATCH_PICK_STATS_COPY.EDIT_PICK : MATCH_PICK_STATS_COPY.MAKE_PICK
    : MATCH_PICK_STATS_COPY.PICK_LOCKED;

  return (
    <View style={styles.container}>
      {/* ── Match header ── */}
      <LinearGradient
        colors={headerGradientColors}
        start={HEADER_GRADIENT_START}
        end={HEADER_GRADIENT_END}
        style={vs.header}
      >
        <View style={vs.topRow}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={MATCH_PICK_STATS_COPY.BACK}
            onPress={onBack}
            style={({ pressed, hovered }) => [
              vs.backBtn,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && vs.backBtnPressed,
              (hovered ?? false) && Platform.OS === 'web' && vs.backBtnPressed,
            ]}
          >
            <ArrowLeft size={18} color={C.ink} />
          </Pressable>

          <View style={vs.headerCenter}>
            {matchInfo ? (
              <Text style={vs.headerDateLabel} numberOfLines={1}>
                {formatKickoffTime(matchInfo.kickoffTime)}
              </Text>
            ) : null}
          </View>

          <View style={vs.spacer} />
        </View>

        {matchInfo ? (
          <View style={vs.teamsGrid}>
            <View style={vs.teamCol}>
              <PickStatsTeamCrest
                countryCode={homeCountryCode}
                kit={homeKit}
                size="default"
                accessibilityLabel={matchInfo.homeTeam}
                testID={MATCH_PICK_STATS_TEST_IDS.crest('home')}
              />
              <Text style={vs.teamName} numberOfLines={2}>{matchInfo.homeTeam}</Text>
            </View>

            <View style={vs.vsCol}>
              {statusInfo ? (
                <View style={[vs.statusChip, statusInfo.isLive ? vs.statusChipLive : vs.statusChipDefault]}>
                  {statusInfo.isLive ? <View style={vs.liveDot} /> : null}
                  <Text style={[vs.statusChipText, statusInfo.isLive ? vs.statusChipTextLive : vs.statusChipTextDefault]}>
                    {statusInfo.label}
                  </Text>
                </View>
              ) : null}
              {isFinished || isLive ? (
                <Text style={[vs.scoreOrVsText, vs.scoreOrVsTextBright]}>
                  {matchInfo.homeScore ?? 0}–{matchInfo.awayScore ?? 0}
                </Text>
              ) : (
                <Text style={[vs.scoreOrVsText, vs.scoreOrVsTextDim]}>vs</Text>
              )}
            </View>

            <View style={vs.teamCol}>
              <PickStatsTeamCrest
                countryCode={awayCountryCode}
                kit={awayKit}
                size="default"
                accessibilityLabel={matchInfo.awayTeam}
                testID={MATCH_PICK_STATS_TEST_IDS.crest('away')}
              />
              <Text style={vs.teamName} numberOfLines={2}>{matchInfo.awayTeam}</Text>
            </View>
          </View>
        ) : null}
      </LinearGradient>

      {/* ── Scrollable body ── */}
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {isError ? (
          <Text style={styles.errorText}>{MATCH_PICK_STATS_COPY.ERROR}</Text>
        ) : data == null ? null : (
          <>
            {/* Odds */}
            {data.odds.length > 0 ? (
              <View style={styles.sectionWrap}>
                <View style={styles.sectionTitleRow}>
                  <Text style={styles.sectionTitle}>{MATCH_PICK_STATS_COPY.ODDS_TITLE}</Text>
                </View>
                <PickStatsOddsTable odds={data.odds} />
              </View>
            ) : null}

            {/* Scope toggle — inside scroll */}
            <PickStatsScopeToggle
              scope={scope}
              groups={groups}
              selectedGroupId={selectedGroupId}
              onScopeChange={onScopeChange}
              onGroupChange={onGroupChange}
            />

            {/* Pick distribution */}
            <View style={styles.sectionWrap}>
              <View style={styles.sectionTitleRow}>
                <Text style={styles.sectionTitle}>{MATCH_PICK_STATS_COPY.PICK_DISTRIBUTION_TITLE}</Text>
                {scope !== 'worldwide' && data.totalPicks > 0 ? (
                  <Text style={styles.sectionPicksBadge}>
                    {data.totalPicks} {MATCH_PICK_STATS_COPY.TOTAL_PICKS}
                  </Text>
                ) : null}
              </View>
              {data.totalPicks === 0 ? (
                <View style={styles.panelCard}>
                  <Text style={styles.noPicksText}>{MATCH_PICK_STATS_COPY.NO_PICKS}</Text>
                </View>
              ) : (
                <PickStatsOutcomePie
                  home={data.outcome.home}
                  draw={data.outcome.draw}
                  away={data.outcome.away}
                  homeCountryCode={homeCountryCode}
                  awayCountryCode={awayCountryCode}
                  homeKit={homeKit}
                  awayKit={awayKit}
                />
              )}
            </View>

            {/* Score heatmap */}
            {data.totalPicks > 0 ? (
              <View style={styles.sectionWrap}>
                <View style={styles.sectionTitleRow}>
                  <Text style={styles.sectionTitle}>{MATCH_PICK_STATS_COPY.SCORE_HEAT_MAP_TITLE}</Text>
                </View>
                <View style={styles.panelCard}>
                  <PickStatsScoreGrid
                    scores={data.scores}
                    homeCountryCode={homeCountryCode}
                    awayCountryCode={awayCountryCode}
                    homeKit={homeKit}
                    awayKit={awayKit}
                  />
                </View>
              </View>
            ) : null}

            {/* Member picks */}
            {showGroupSections && data.memberPicks && data.memberPicks.length > 0 ? (
              <View style={vs.memberPicksWrap}>
                <View style={[styles.sectionTitleRow, vs.memberPicksTitleRow]}>
                  <Text style={styles.sectionTitle}>
                    {MATCH_PICK_STATS_COPY.MEMBER_PICKS_TITLE}
                  </Text>
                </View>
                <PickStatsMemberPicksRow
                  memberPicks={data.memberPicks}
                  actualHomeScore={matchInfo?.fullTimeHomeScore ?? matchInfo?.homeScore ?? null}
                  actualAwayScore={matchInfo?.fullTimeAwayScore ?? matchInfo?.awayScore ?? null}
                />
              </View>
            ) : null}
          </>
        )}
      </ScrollView>

      {showPickFooter ? (
        <View style={[vs.pickFooterWrap, vs.pickFooterWrapPointerEvents]}>
          <View style={vs.pickFooterCard}>
            <View style={vs.pickFooterCopy}>
              <Text style={vs.pickFooterEyebrow}>{pickFooterTitle}</Text>
              <Text style={vs.pickFooterText} numberOfLines={1}>{pickFooterText}</Text>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={pickFooterButtonLabel}
              disabled={!isPickEditable}
              onPress={onEditPick}
              style={({ pressed, hovered }) => [
                vs.pickFooterButton,
                !isPickEditable && vs.pickFooterButtonDisabled,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && isPickEditable && vs.pickFooterButtonPressed,
                (hovered ?? false) && Platform.OS === 'web' && isPickEditable && vs.pickFooterButtonPressed,
              ]}
            >
              <Text style={[vs.pickFooterButtonText, !isPickEditable && vs.pickFooterButtonTextDisabled]}>
                {pickFooterButtonLabel}
              </Text>
            </Pressable>
          </View>
        </View>
      ) : null}
    </View>
  );
};
